<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';

import { ApiClient } from '@/services/ApiClient';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { TransitionFade } from '@nzyme/vue-transitions/TransitionFade.jsx';
import { TransitionSlide } from '@nzyme/vue-transitions/TransitionSlide.jsx';
import { provideContext } from '@nzyme/vue-utils/context.js';
import { reactive } from '@nzyme/vue-utils/reactivity/reactive.js';
import { useLoadingContext } from '@nzyme/vue-utils/useLoadingContext.js';
import { ActorClient } from '@sigma/actors-client/ActorClient.js';
import { ChatActor } from '@sigma/api-core/actors/ChatActor.js';
import type { ChatEventViewModel } from '@sigma/chat-common/ChatEventViewModel.js';
import { getPatientCanShowChatInput } from '@sigma/chat-common/utils/getPatientCanShowChatInput.js';
import { getPatientChatState } from '@sigma/chat-common/utils/getPatientChatState.js';
import { isPatientAuthoredEvent } from '@sigma/chat-common/utils/isPatientAuthoredEvent.js';
import { isPatientVisibleEvent } from '@sigma/chat-common/utils/isPatientVisibleEvent.js';
import { ChatStore } from '@sigma/client/chat/ChatStore.js';
import { useChat } from '@sigma/client/chat/useChat.js';
import { useChatComposer } from '@sigma/client/chat/useChatComposer.js';
import { NEVER_READ_SEQ, useUnreadDivider } from '@sigma/client/chat/useUnreadDivider.js';
import { FileMetadataStore } from '@sigma/client/files/FileMetadataStore.js';
import { useFilesUpload } from '@sigma/client/files/useFilesUpload.js';
import { ActorConnectionService } from '@sigma/client/services/ActorConnectionService.js';
import { BrowserTabService } from '@sigma/client/services/BrowserTabService.js';
import type { Id } from '@sigma/common/schema/Id.js';
import type { FileInput } from '@sigma/files-common/FileInput.js';
import { ChatGetEndpoint } from '@sigma/patient-api/chat/ChatGetEndpoint.js';
import ChatAttachFileButton from '@sigma/ui/chat/ChatAttachFileButton.vue';
import ChatEventContainer from '@sigma/ui/chat/ChatEventContainer.vue';
import ChatInput from '@sigma/ui/chat/ChatInput.vue';
import NewMessagesIndicator from '@sigma/ui/chat/NewMessagesIndicator.vue';
import { provideLayout } from '@sigma/ui/layout/Layout.jsx';
import LoaderOverlay from '@sigma/ui/LoaderOverlay.vue';

import { ChatContext } from './ChatContext.js';
import ChatLayout from './ChatLayout.vue';
import { ChatListStore } from './ChatListStore.js';
import { ChatMessage } from './ChatMessage.js';
import ChatNotFound from './ChatNotFound.vue';
import ChatOfflineFooter from './ChatOfflineFooter.vue';
import * as l from './ChatView.loc.js';

const props = defineProps<{
  /**
   * The chat to display. Provided via router `props: true`.
   * The whole component re-renders on chat change, so this never mutates mid-lifecycle.
   */
  chatId: Id;
}>();

const apiClient = useService(ApiClient);
const actorClient = useService(ActorClient);
const actorConnection = useService(ActorConnectionService);
const browserTab = useService(BrowserTabService);
const chatListStore = useService(ChatListStore);
const chatStore = useService(ChatStore);
const fileMetadataStore = useService(FileMetadataStore);
const loadingCtx = useLoadingContext({ delay: 100 });
const loadedChat = loadingCtx.start();
const t = useTranslate();

provideLayout(ChatLayout);

const offline = computed(() => {
  return actorConnection.connected === false || browserTab.status === 'offline';
});

const chat = useChat(props.chatId);

const filesUpload = useFilesUpload({
  uploadTarget: { chatId: props.chatId },
  cacheImagePreview: (fileId, blobUrl) => {
    fileMetadataStore.set(fileId, { id: fileId, url: blobUrl });
  },
});
const chatInputDisabled = computed(() => !filesUpload.filesAreValid);

async function handleFileUpload(filesToUpload: FileInput[]) {
  await filesUpload.uploadFiles(filesToUpload);
}

const chatCtx = reactive<ChatContext>({
  chat,
});

// Effective read seq at the time the chat was opened (includes queued read marks).
// Resolved asynchronously in onMounted; undefined while loading suppresses the divider.
const initialReadSeq = ref<number>();

const nearBottom = ref(true);
const { dividerSeq, onEventRead: onDividerEventRead } = useUnreadDivider({
  chat,
  initialReadSeq,
  // For a chat the patient just started, the start view recorded the open-time before the agent
  // was pinged, so the agent's first message counts as witnessed-live. Falls back to the composable
  // default (now) for a direct open / re-entry, where the mount time is the correct baseline.
  openedAt: chatStore.consumeOpenedAt(props.chatId),
  canAnchor: isPatientVisibleEvent,
  isOwn: isPatientAuthoredEvent,
  isWatching: () => browserTab.status !== 'background' && nearBottom.value,
});

const chatEnded = computed(() => {
  return getPatientChatState(chat.events).status === 'ENDED';
});

const showChatNotFound = computed(() => {
  return chat.error?.type === 'not-found';
});

provideContext(ChatContext, chatCtx);

/**
 * Get the effective last read seq for a chat (including read marks queued for sync).
 * Uses the ChatListStore when it knows the chat, otherwise fetches from API.
 */
async function getSeqRead(id: Id): Promise<number | undefined> {
  const cached = chatListStore.chats?.find(c => c.id === id);
  if (cached) {
    // May be undefined when the chat was never read — the caller coerces to 0.
    return chatListStore.getReadSeq(id);
  }

  const result = await apiClient(ChatGetEndpoint, { chatId: id });
  return result?.eventSeqRead;
}

onMounted(async () => {
  void actorConnection.connect();

  // Coerce "never read yet" (null/undefined from server) to the never-read sentinel so every
  // event — including the 0-based first one — is treated as unread on first open. (0 is a valid
  // read position for a 0-based seq, so it cannot double as "never read".) Keeping
  // `initialReadSeq` undefined while the lookup is in-flight still suppresses the indicator.
  initialReadSeq.value = (await getSeqRead(props.chatId)) ?? NEVER_READ_SEQ;
  void actorClient.actor(ChatActor).requests.ping(props.chatId);

  await chat.load();

  loadedChat();
});

const { message, isAnswering, send } = useChatComposer({
  chat,
  chatId: props.chatId,
  target: 'PATIENT',
  author: { type: 'PATIENT' },
  markRead: (id, seq) => chatListStore.markRead(id, seq),
  filesUpload: {
    submitFiles: () => filesUpload.submitFiles(),
    clearFiles: () => filesUpload.clearFiles(),
  },
});

const showInput = computed(() => chat.loaded && getPatientCanShowChatInput(chat.events));

function onEventRead(event: ChatEventViewModel) {
  chatListStore.markRead(event.chatId, event.seq);
  onDividerEventRead(event.seq);
}
</script>

<template>
  <main
    ref="element"
    :class="css.page"
    :inert="loadingCtx.loading"
  >
    <TransitionFade>
      <LoaderOverlay
        v-if="!loadingCtx.loadedOnce"
        :class="css.loaderOverlay"
      />
    </TransitionFade>

    <ChatNotFound v-if="showChatNotFound" />
    <div
      v-else
      :class="css.chat"
    >
      <!-- Chat events -->
      <ChatEventContainer
        v-if="chat.events.length"
        v-model:near-bottom="nearBottom"
        :events="chat.events"
        :class="css.messages"
        scroll-to-new
        @event-read="onEventRead"
      >
        <template
          v-for="event in chat.events"
          :key="event.seq"
        >
          <NewMessagesIndicator v-if="event.seq === dividerSeq" />
          <ChatMessage
            :event="event"
            :chat="chat"
          />
        </template>
      </ChatEventContainer>

      <div :class="css.inputWrapper">
        <TransitionSlide
          direction="up"
          fade
        >
          <ChatOfflineFooter v-if="offline && !chatEnded" />

          <ChatInput
            v-else-if="showInput"
            v-model="message"
            :files="isAnswering ? undefined : filesUpload.filesToShow"
            :disabled="isAnswering ? false : chatInputDisabled"
            :placeholder="isAnswering ? t(l.typeAnswer) : undefined"
            :class="css.input"
            @send="send"
            @delete-file="filesUpload.deleteFile"
          >
            <ChatAttachFileButton
              v-if="!isAnswering"
              @upload="handleFileUpload"
            />
          </ChatInput>
        </TransitionSlide>
      </div>
    </div>
  </main>
</template>

<style lang="scss" module="css">
.page {
  display: flex;
  flex-direction: column;
  padding-top: $main-header-height;
  flex: 1;
}

.chat {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.workingHoursInfo {
  margin-top: $spacing-24;
}

.messages {
  padding-top: $spacing-20;
}

.inputWrapper {
  display: flex;
  position: sticky;
  bottom: 0;
  z-index: 2;

  // This is just a container to preserve the space for the input
  // We don't want it to grab clicks when sticked to bottom
  pointer-events: none;

  flex-direction: column;
  justify-content: flex-end;
  min-height: 120px;
  margin-top: auto;

  @include media-min-md {
    margin-top: $spacing-24;
  }
}

.input {
  pointer-events: auto;

  @include media-min-md {
    padding-bottom: $spacing-20;
  }
}

.loaderOverlay {
  background: $color-doctor-50;
  position: fixed;
}
</style>
